import React, { useEffect, useState } from 'react';
import { Button, Drawer, Field, Input, Select, Table, Tabs } from '@itds/elements';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import Api from '../../../../utils/Api';
import PropTypes from 'prop-types';

function Investments({ version }) {
  const params = useParams();

  const [investment, setInvestment] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('IUL');
  const [fundType, setFundType] = useState('Fixed');
  useEffect(() => {
    async function getInvestments() {
      if (!version?.id) return;

      try {
        const response = await Api.loadLifeInvestments(version.id);

        setInvestments(response?.data || []); // Load products for filtering based on current tab
        const productsResponse = await Api.loadProducts({
          carrier_id: params.id,
          data_version_id: params.version_id,
          product_type: activeTab
        });

        setProducts(productsResponse?.data || []);
      } catch (error) {
        console.error('Error loading investments:', error);
      } finally {
        // Loading complete
      }
    }
    getInvestments();
  }, [version?.id, params.id, params.version_id, activeTab]);
  function getValue(eOrVal) {
    return eOrVal?.target?.value ?? eOrVal?.value ?? eOrVal;
  }

  function handleInputChange(name) {
    return function onChange(eOrVal) {
      const v = getValue(eOrVal);

      setInvestment(prev => ({ ...prev, [name]: v }));
    };
  }

  function handleBooleanChange(name) {
    return function onChange(eOrVal) {
      const v = getValue(eOrVal);

      setInvestment(prev => ({ ...prev, [name]: String(v) === 'true' }));
    };
  }

  function handleProductSelection(productId) {
    return function onChange(e) {
      const isChecked = e.target.checked;

      if (isChecked) {
        setSelectedProducts(prev => [...prev, productId]);
      } else {
        setSelectedProducts(prev => prev.filter(id => id !== productId));
      }
    };
  }
  function handleFundTypeChange(eOrVal) {
    const value = getValue(eOrVal);

    setFundType(value);
    setInvestment(prev => ({ ...prev, fund_type: value }));
  }

  function handleSave() {
    let payload;

    // Helper function to process states
    const processStateAvailability = statesApproved => {
      if (Array.isArray(statesApproved)) {
        return statesApproved;
      }
      if (statesApproved) {
        return statesApproved.split(',').map(s => s.trim());
      }
      return [];
    };

    if (fundType === 'Fixed') {
      // Fixed fund payload structure
      payload = {
        product_type: activeTab.toUpperCase(),
        product_type_detail: investment?.product_type_detail || 'To age 70',
        carrier_id: parseInt(params.id, 10),
        data_version_id: parseInt(version?.id, 10),
        fund_type: fundType,
        rate: parseFloat(investment?.rate) || 0,
        state_availability: processStateAvailability(investment?.states_approved),
        mva: Boolean(investment?.mva),
        is_active: investment?.is_active !== false,
        product_ids: selectedProducts || []
      };
    } else {
      // Indexed fund payload structure
      payload = {
        carrier_id: parseInt(params.id, 10),
        data_version_id: parseInt(version?.id, 10),
        product_type: activeTab.toUpperCase(),
        product_type_detail: investment?.product_type_detail || 'Indexed Universal Life',
        fund_type: fundType,
        band_des: investment?.band_description || '',
        crediting_strategy: investment?.crediting_strategy || '',
        crediting_frequency: investment?.crediting_frequency || '',
        rate_term: parseInt(investment?.rate_term, 10) || 0,
        rate: parseFloat(investment?.rate) || 0,
        gtd_min_rate: parseFloat(investment?.guaranteed_minimum) || 0,
        state_availability: processStateAvailability(investment?.states_approved),
        index: investment?.index_name || '',
        ticker_symbol: investment?.ticker_symbol || '',
        mva: Boolean(investment?.mva),
        is_active: investment?.is_active !== false,
        product_ids: selectedProducts || []
      };
    }

    // Add ID to payload when editing
    if (investment?.id) {
      payload = { ...payload, id: investment.id };
    }

    if (!investment?.id) {
      Api.saveNewLifeInvestment(payload)
        .then(() => {
          setIsDrawerOpen(false);
          loadInvestments();
        })
        .catch(error => {
          console.error('Error saving investment:', error);
        });
    } else {
      // For existing funds, we'll update the fund
      Api.saveEditLifeInvestment(payload)
        .then(() => {
          setIsDrawerOpen(false);
          loadInvestments();
        })
        .catch(error => {
          console.error('Error saving investment:', error);
        });
    }
  }
  function loadInvestments() {
    if (!version?.id) return;

    Api.loadLifeInvestments(version.id)
      .then(response => {
        setInvestments(response?.data || []);
      })
      .catch(error => {
        console.error('Error loading investments:', error);
      });
  }
  function openCreateNewDrawer() {
    setInvestment({
      product_type: activeTab,
      fund_type: 'Fixed',
      data_version_id: version?.id,
      carrier_id: params.id,
      is_active: true
    });
    setFundType('Fixed');
    setSelectedProducts([]);
    setIsDrawerOpen(true);

    // Load products for the current tab when opening drawer
    loadProductsForTab(activeTab);
  }

  function closeDrawer() {
    setIsDrawerOpen(false);
  }
  function onRowClick(row) {
    setInvestment({
      ...row,
      // Map API response fields to form fields
      states_approved: row.state_availability,
      index_name: row.index,
      band_description: row.band_des,
      guaranteed_minimum: row.gtd_min_rate
    });
    setFundType(row.fund_type || 'Fixed');
    setSelectedProducts(row.product_ids || []);
    setIsDrawerOpen(true);
  }
  function getFilteredInvestments() {
    // Filter investments by active tab, also show investments with null product_type in both tabs
    let filtered = investments.filter(
      inv => inv.product_type === activeTab || inv.product_type === null
    );

    if (selectedProduct) {
      filtered = filtered.filter(
        inv => inv.product_ids && inv.product_ids.includes(parseInt(selectedProduct, 10))
      );
    }

    return filtered;
  }
  function getIulColumns() {
    return [
      {
        id: 'fund_type',
        label: 'Fund Type',
        render: row => row.fund_type || 'N/A'
      },
      {
        id: 'crediting_strategy',
        label: 'Crediting Strategy',
        render: row => row.crediting_strategy || 'N/A'
      },
      {
        id: 'rate',
        label: 'Rate',
        render: row => (row.rate ? `${row.rate}%` : 'N/A')
      },
      {
        id: 'state_availability',
        label: 'States approved',
        render: row => {
          if (Array.isArray(row.state_availability)) {
            return row.state_availability.join(', ');
          }
          return row.state_availability || 'N/A';
        }
      },
      {
        id: 'index',
        label: 'Index',
        render: row => row.index || 'N/A'
      },
      {
        id: 'mva',
        label: 'MVA',
        render: row => (row.mva ? 'True' : 'False')
      },
      {
        id: 'updated_at',
        label: 'Date Updated',
        render: row => (row.updated_at ? format(new Date(row.updated_at), 'MM/dd/yyyy') : 'N/A')
      }
    ];
  }
  function getVulColumns() {
    return [
      {
        id: 'fund_type',
        label: 'Fund Type',
        render: row => row.fund_type || 'N/A'
      },
      {
        id: 'product_type_detail',
        label: 'Product Type Detail',
        render: row => row.product_type_detail || 'N/A'
      },
      {
        id: 'rate',
        label: 'Rate',
        render: row => (row.rate ? `${row.rate}%` : 'N/A')
      },
      {
        id: 'state_availability',
        label: 'States approved',
        render: row => {
          if (Array.isArray(row.state_availability)) {
            return row.state_availability.join(', ');
          }
          return row.state_availability || 'N/A';
        }
      },
      {
        id: 'mva',
        label: 'MVA',
        render: row => (row.mva ? 'True' : 'False')
      },
      {
        id: 'updated_at',
        label: 'Date Updated',
        render: row => (row.updated_at ? format(new Date(row.updated_at), 'MM/dd/yyyy') : 'N/A')
      }
    ];
  }
  const tabs = [
    {
      label: 'IUL',
      onClick: () => {
        setActiveTab('IUL');
        loadProductsForTab('IUL');
      },
      isActive: activeTab === 'IUL'
    },
    {
      label: 'VUL',
      onClick: () => {
        setActiveTab('VUL');
        loadProductsForTab('VUL');
      },
      isActive: activeTab === 'VUL'
    }
  ];
  async function loadProductsForTab(tabType) {
    if (!params.id || !params.version_id) return;

    try {
      const productsResponse = await Api.loadProducts({
        carrier_id: params.id,
        data_version_id: params.version_id,
        product_type: tabType
      });

      setProducts(productsResponse?.data || []);
    } catch (error) {
      console.error('Error loading products for tab:', error);
    }
  }

  const filteredInvestments = getFilteredInvestments();
  const columns = activeTab === 'IUL' ? getIulColumns() : getVulColumns();

  return (
    <>
      {' '}
      <div style={{ marginBottom: 20 }}>
        <Tabs tabs={tabs} />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 30
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label>Product Filter:</label>
          <Select
            onChange={e => setSelectedProduct(getValue(e))}
            options={[
              { value: '', label: 'All Products' },
              ...products.map(p => ({
                value: p.id,
                label: p.product_name || `Product ${p.id}`
              }))
            ]}
            style={{ minWidth: 200 }}
            value={selectedProduct}
          />
        </div>{' '}
        <Button onClick={openCreateNewDrawer} style={{}}>
          New Fund
        </Button>
      </div>
      <Table columns={columns} data={filteredInvestments} onRowClick={onRowClick} />
      <Drawer
        alignRight
        title={investment?.id ? 'Edit Fund' : 'New Fund'}
        show={isDrawerOpen}
        onClose={closeDrawer}
        footer={
          <Button style={{}} onClick={handleSave}>
            {investment?.id ? 'Update Fund' : 'Create Fund'}
          </Button>
        }
      >
        {' '}
        {/* Fund Type Selection */}
        <Field label='Fund Type' style={{}}>
          <Select
            style={{}}
            value={fundType}
            onChange={handleFundTypeChange}
            options={[
              { value: 'Fixed', label: 'Fixed' },
              { value: 'Indexed', label: 'Indexed' }
            ]}
          />
        </Field>
        {/* Product Type Detail */}
        <Field label='Product Type Detail' style={{}}>
          <Input
            value={investment?.product_type_detail || ''}
            onChange={handleInputChange('product_type_detail')}
            placeholder='e.g., To age 70'
          />
        </Field>
        {/* Fixed Fund Fields */}
        {fundType === 'Fixed' && (
          <>
            <Field label='Rate (%)' style={{}}>
              <Input
                value={investment?.rate || ''}
                onChange={handleInputChange('rate')}
                type='number'
                step='0.01'
              />
            </Field>

            <Field label='States Approved' style={{}}>
              <Input
                value={investment?.states_approved || ''}
                onChange={handleInputChange('states_approved')}
                placeholder='e.g., CA,NY,TX,FL'
              />
            </Field>

            <Field label='MVA' style={{}}>
              <Select
                style={{}}
                value={investment?.mva ? 'true' : 'false'}
                onChange={handleBooleanChange('mva')}
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' }
                ]}
              />
            </Field>
          </>
        )}{' '}
        {/* Indexed Fund Fields */}
        {fundType === 'Indexed' && (
          <>
            <Field label='Rate Term' style={{}}>
              <Input
                value={investment?.rate_term || ''}
                onChange={handleInputChange('rate_term')}
              />
            </Field>

            <Field label='Crediting Strategy' style={{}}>
              <Input
                value={investment?.crediting_strategy || ''}
                onChange={handleInputChange('crediting_strategy')}
              />
            </Field>

            <Field label='Crediting Frequency' style={{}}>
              <Select
                style={{}}
                value={investment?.crediting_frequency || ''}
                onChange={handleInputChange('crediting_frequency')}
                options={[
                  { value: '', label: 'Select Frequency' },
                  { value: 'Monthly', label: 'Monthly' },
                  { value: 'Quarterly', label: 'Quarterly' },
                  { value: 'Semi-Annually', label: 'Semi-Annually' },
                  { value: 'Annually', label: 'Annually' }
                ]}
              />
            </Field>

            <Field label='Rate (%)' style={{}}>
              <Input
                value={investment?.rate || ''}
                onChange={handleInputChange('rate')}
                type='number'
                step='0.01'
              />
            </Field>

            <Field label='Index' style={{}}>
              <Input
                value={investment?.index_name || ''}
                onChange={handleInputChange('index_name')}
              />
            </Field>

            <Field label='Ticker Symbol' style={{}}>
              <Input
                value={investment?.ticker_symbol || ''}
                onChange={handleInputChange('ticker_symbol')}
              />
            </Field>

            <Field label='Guaranteed Minimum (%)' style={{}}>
              <Input
                value={investment?.guaranteed_minimum || ''}
                onChange={handleInputChange('guaranteed_minimum')}
                type='number'
                step='0.01'
              />
            </Field>

            <Field label='States Approved' style={{}}>
              <Input
                value={investment?.states_approved || ''}
                onChange={handleInputChange('states_approved')}
                placeholder='e.g., CA,NY,TX,FL'
              />
            </Field>

            <Field label='MVA' style={{}}>
              <Select
                style={{}}
                value={investment?.mva ? 'true' : 'false'}
                onChange={handleBooleanChange('mva')}
                options={[
                  { value: 'true', label: 'True' },
                  { value: 'false', label: 'False' }
                ]}
              />
            </Field>

            <Field label='Band Description' style={{}}>
              <Input
                value={investment?.band_description || ''}
                onChange={handleInputChange('band_description')}
              />
            </Field>
          </>
        )}{' '}
        {/* Product Selection with Checkboxes */}
        <Field label={`Select ${activeTab} Products`} style={{}}>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {' '}
            {products
              .filter(p => p.product_type?.includes(activeTab) || p.investment_type === activeTab)
              .map(product => (
                <div
                  key={product.id}
                  style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}
                >
                  <input
                    type='checkbox'
                    id={`product-${product.id}`}
                    checked={selectedProducts.includes(product.id)}
                    onChange={handleProductSelection(product.id)}
                    style={{ marginRight: 8 }}
                  />
                  <label htmlFor={`product-${product.id}`}>
                    {product.product_name || `Product ${product.id}`}
                  </label>
                </div>
              ))}
          </div>
        </Field>
        <Field label='Active' style={{}}>
          <Select
            style={{}}
            value={investment?.is_active !== false ? 'true' : 'false'}
            onChange={handleBooleanChange('is_active')}
            options={[
              { value: 'true', label: 'Active' },
              { value: 'false', label: 'Inactive' }
            ]}
          />
        </Field>
      </Drawer>
    </>
  );
}

Investments.propTypes = {
  version: PropTypes.object
};

export default Investments;
